import { usePocQuery } from 'core/custom-hooks/use-poc-query.hook';
import { settingsKeys } from 'core/query-client/keys/settings.keys';
import { executeRequest } from 'core/utils/backend-service';
import { DeviceTypeDetails } from 'modules/devices/domain/api/device-type.api';
import {
    getDeviceConfigurationInstance,
    getLayoutSections,
} from 'modules/settings/pods/settings-devices/settings-devices-configuration/service/settings-devices-configuration.service';
import React, { useEffect, useMemo, useState } from 'react';

import {
    DeviceConfigurationInstance,
    DeviceLayoutConfigSectionDetails,
    DeviceLayoutConfiguration,
    DeviceLayoutDependencies,
} from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-configuration-layout/settings-devices-configuration-layout.model';
import { DeviceConfigurationInfo } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-configuration-list/settings-devices-configuration-list.model';
import { ConfigurationDetailContext, ConfigurationDetailContextType } from '../configuration-detail.context';
import { DeviceConfigurationValidity } from '../device-configuration.type';

export interface ConfigurationDetailProviderProps extends React.PropsWithChildren {
    deviceType?: DeviceTypeDetails;
    deviceConfiguration?: DeviceConfigurationInfo;
}

export const ConfigurationDetailProvider: React.FC<ConfigurationDetailProviderProps> = ({
    children,
    deviceConfiguration,
    deviceType,
}) => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [hasConfigChanged, setHasConfigChanged] = useState<boolean>(false);
    const [layoutSections, setLayoutSections] = useState<DeviceLayoutConfiguration | undefined>(undefined);
    const [dependencies, setDependencies] = useState<DeviceLayoutDependencies[]>([]);
    const [layoutSectionSelected, setLayoutSectionSelected] = useState<string | undefined>(undefined);
    const [sectionDetails, setSectionDetails] = useState<DeviceLayoutConfigSectionDetails | undefined>(undefined);
    const [deviceConfigurationInstance, setDeviceConfigurationInstance] = useState<DeviceConfigurationInstance>({
        properties: {},
    } as DeviceConfigurationInstance);
    const [deviceConfigurationValidation, setDeviceConfigurationValidation] = useState<DeviceConfigurationValidity>({});

    const value: ConfigurationDetailContextType = useMemo(
        () => ({
            layoutSections,
            dependencies,
            layoutSectionSelected,
            sectionDetails,
            isLoading,
            deviceConfigurationInstance,
            hasConfigChanged,
            deviceConfigurationValidation,
            setHasConfigChanged,
            setIsLoading,
            setSectionDetails,
            setLayoutSectionSelected,
            setDeviceConfigurationValidation,
            setDeviceConfigurationInstance: (t) => setDeviceConfigurationInstance((prev) => ({ ...prev, ...t })),
        }),
        [
            layoutSections,
            layoutSectionSelected,
            sectionDetails,
            deviceConfigurationInstance,
            hasConfigChanged,
            isLoading,
            deviceConfigurationValidation,
        ]
    );

    const { data: layoutSectionsData, isFetching: isFetchingSections } = usePocQuery({
        queryKey: settingsKeys.configDetail(`layout-${deviceType?.id}`),
        queryFn: () => executeRequest(() => getLayoutSections(deviceType!.id)),
        enabled: deviceType !== undefined,
    });

    const { data: instanceData, isFetching: isFetchingInstance } = usePocQuery({
        queryKey: settingsKeys.configDetail(`instance-${deviceConfiguration?.id}`),
        queryFn: () => executeRequest(() => getDeviceConfigurationInstance(deviceConfiguration!.id)),
        // Gate on the value the queryFn actually consumes — a deviceType can be selected before a
        // configuration is, and firing with an undefined id would hit the backend wrongly.
        enabled: deviceConfiguration !== undefined,
    });

    // Seed the locally-mutable state from the queries (the instance + selected section are edited downstream).
    useEffect(() => {
        if (layoutSectionsData) {
            setLayoutSections(layoutSectionsData);
            setDependencies(layoutSectionsData.dependencies);
            setLayoutSectionSelected(layoutSectionsData?.sections[0]?.id);
        }
    }, [layoutSectionsData]);

    useEffect(() => {
        if (instanceData) {
            setDeviceConfigurationInstance(instanceData);
        }
    }, [instanceData]);

    useEffect(() => {
        setIsLoading(isFetchingSections || isFetchingInstance);
    }, [isFetchingSections, isFetchingInstance]);

    return <ConfigurationDetailContext.Provider value={value}>{children}</ConfigurationDetailContext.Provider>;
};
